import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, PartyPopper } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/lib/logger';
import { trackPurchase, trackEvent, FUNNEL_EVENTS } from '@/lib/analytics';

type SessionSnapshot = {
  status: string;
  plan: string | null;
  priceIdKey: string | null;
  value: number;
  currency: string;
  trialEnd: number | null;
  transactionId: string;
};

/**
 * /purchase-success — shown after a Stripe checkout that resulted in an
 * immediate paid subscription (trial skipped, or Starter one-time).
 *
 * Fires the client-side GA4 purchase event and Google Ads website purchase
 * conversion. Trial-to-paid purchases are separately uploaded server-side
 * from the stripe-webhook invoice.paid handler (PR 2) with the captured gclid.
 *
 * Dedupe flag lives on organizations.purchase_tracked_at (per plan Q1).
 */
const PurchaseSuccess = () => {
  const navigate = useNavigate();
  const { user, fetchSubscriptionStatus } = useAuth();
  const [snapshot, setSnapshot] = useState<SessionSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) {
      logger.warn('PurchaseSuccess: missing session_id in URL');
      setError('Missing checkout session. Redirecting…');
      const timer = setTimeout(() => navigate('/', { replace: true }), 1500);
      return () => clearTimeout(timer);
    }

    let cancelled = false;

    (async () => {
      try {
        const { data: verifyData, error: verifyErr } = await supabase.functions.invoke(
          'verify-checkout-session',
          { body: { session_id: sessionId } },
        );
        if (cancelled) return;
        if (verifyErr) {
          logger.error('PurchaseSuccess: verify-checkout-session failed', verifyErr);
          setError('Could not verify your checkout.');
          return;
        }
        const data = verifyData as SessionSnapshot;
        setSnapshot(data);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const db = supabase as any;

        const { data: orgRows, error: orgErr } = await db
          .from('organization_users')
          .select('organization_id')
          .eq('user_id', user.id)
          .limit(1);
        if (cancelled) return;
        if (orgErr || !orgRows?.[0]?.organization_id) {
          logger.error('PurchaseSuccess: could not resolve organization', orgErr);
          return;
        }
        const organizationId = orgRows[0].organization_id;

        const { data: org, error: orgReadErr } = await db
          .from('organizations')
          .select('purchase_tracked_at')
          .eq('id', organizationId)
          .single();
        if (cancelled) return;
        if (orgReadErr) {
          logger.error('PurchaseSuccess: org read failed', orgReadErr);
          return;
        }

        if (!org?.purchase_tracked_at) {
          const { error: updateErr } = await db
            .from('organizations')
            .update({ purchase_tracked_at: new Date().toISOString() })
            .eq('id', organizationId);
          if (updateErr) {
            logger.error('PurchaseSuccess: failed to set purchase_tracked_at; aborting fire', updateErr);
            return;
          }

          await trackPurchase({
            value: data.value,
            currency: data.currency,
            transactionId: data.transactionId,
            email: user.email ?? undefined,
          });
          trackEvent(FUNNEL_EVENTS.CHECKOUT_SUCCESS, { tier: data.priceIdKey ?? 'unknown' });
          trackEvent(FUNNEL_EVENTS.PAID_ACTIVATION, { tier: data.priceIdKey ?? 'unknown' });
        }

        // Refresh subscription state so the dashboard reflects the new tier.
        // The stripe-webhook has already written org billing state by now.
        fetchSubscriptionStatus();
      } catch (err) {
        if (cancelled) return;
        logger.error('PurchaseSuccess: unexpected error', err);
        setError('Something went wrong.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, navigate, fetchSubscriptionStatus]);

  const onContinue = () => {
    navigate('/dashboard', { replace: true });
  };

  const valueDisplay = snapshot
    ? new Intl.NumberFormat(undefined, { style: 'currency', currency: snapshot.currency }).format(snapshot.value)
    : null;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-grow container mx-auto px-4 py-12 flex items-center justify-center">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
              <PartyPopper className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">Thank you!</CardTitle>
            <CardDescription>
              {snapshot
                ? valueDisplay
                  ? `Your ${snapshot.plan ?? 'plan'} is active. You were charged ${valueDisplay}.`
                  : `Your ${snapshot.plan ?? 'plan'} is active.`
                : error ?? 'Confirming your purchase…'}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            <Button onClick={onContinue} size="lg" disabled={!snapshot}>
              {snapshot ? 'Go to dashboard' : <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading…</>}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PurchaseSuccess;
